/*
 * ww-celebrate.js  — site-wide celebration for promo codes
 *
 * Fires a red/white/blue fireworks + confetti burst the moment a "celebrate"
 * code (e.g. USA250) actually becomes APPLICABLE on the cart — i.e. the code
 * is present AND the qualifying product is in the cart so Shopify is really
 * discounting it.
 *
 * Because it watches cart STATE (not a button click), it works no matter how
 * the code arrives:
 *   - typed into the promo field on the cart page,
 *   - carried in on a discount URL (/discount/USA250?redirect=...),
 *   - or satisfied later by adding the qualifying kit from the cart drawer.
 *
 * It celebrates ONCE per satisfied code (remembered in localStorage), and can
 * celebrate again if the code is lost (kit removed) and later re-satisfied.
 *
 * Load this GLOBALLY (theme.liquid, before </body>):
 *   <script src="{{ 'ww-celebrate.js' | asset_url }}" defer></script>
 */
(function () {
  'use strict';

  // Codes that trigger the celebration. Add more if you want.
  var CELEBRATE_CODES = ['USA250'];
  var STORE_KEY = 'wwCelebrated';

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  root = root.replace(/\/+$/, '');
  var CART_URL = root + '/cart.js';

  function up(s) { return String(s).toUpperCase(); }
  function isCelebrate(code) {
    var lc = String(code).toLowerCase();
    return CELEBRATE_CODES.some(function (c) { return c.toLowerCase() === lc; });
  }

  // Which celebrate codes we've already celebrated (persisted across pages).
  function getCelebrated() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY)) || []; }
    catch (e) { return []; }
  }
  function setCelebrated(list) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(list)); } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Fireworks + confetti (self-contained canvas, no libraries, no assets).
  // ---------------------------------------------------------------------------
  function launchFireworks() {
    if (typeof document === 'undefined' || !document.body) return;
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    } catch (e) {}
    if (document.querySelector('.ww-fireworks')) return; // don't stack runs

    var canvas = document.createElement('canvas');
    canvas.className = 'ww-fireworks';
    var st = canvas.style;
    st.position = 'fixed';
    st.left = '0';
    st.top = '0';
    st.width = '100%';
    st.height = '100%';
    st.pointerEvents = 'none';
    st.zIndex = '2147483000';
    document.body.appendChild(canvas);

    var ctx = canvas.getContext('2d');
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    function vw() { return window.innerWidth; }
    function vh() { return window.innerHeight; }
    function resize() {
      canvas.width = Math.floor(vw() * dpr);
      canvas.height = Math.floor(vh() * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    window.addEventListener('resize', resize);

    var COLORS = ['#c8102e', '#ffffff', '#0a3161']; // Old Glory red, white, blue
    var GRAVITY = 0.05;
    var shells = [];
    var particles = [];
    var confetti = [];
    var start = performance.now();
    var LAUNCH_MS = 4200;
    var TOTAL_MS = 5600;
    var lastLaunch = 0;
    var lastConfetti = 0;

    function spawnConfetti(n, fromTop) {
      for (var i = 0; i < n; i++) {
        confetti.push({
          x: Math.random() * vw(),
          y: fromTop ? (-20 - Math.random() * vh() * 0.5) : (Math.random() * vh() * 0.25),
          vx: (Math.random() - 0.5) * 1.2,
          vy: 1.6 + Math.random() * 2.6,
          w: 5 + Math.random() * 5,
          h: 8 + Math.random() * 7,
          rot: Math.random() * Math.PI * 2,
          vrot: (Math.random() - 0.5) * 0.26,
          wob: Math.random() * Math.PI * 2,
          dwob: 0.03 + Math.random() * 0.04,
          amp: 0.6 + Math.random() * 1.4,
          color: COLORS[Math.random() * COLORS.length | 0]
        });
      }
    }

    function endFade(e) {
      var fo = 900;
      if (e > TOTAL_MS - fo) return Math.max(0, (TOTAL_MS - e) / fo);
      return 1;
    }

    spawnConfetti(110, true); // opening burst from the top

    function spawnShell() {
      var x = vw() * (0.12 + Math.random() * 0.76);
      var targetY = vh() * (0.18 + Math.random() * 0.34);
      var dist = vh() - targetY;
      shells.push({ x: x, y: vh() + 8, vy: -(dist / 26) - Math.random() * 2, targetY: targetY });
    }

    function explode(x, y) {
      var count = 44 + (Math.random() * 26 | 0);
      var base = COLORS[Math.random() * COLORS.length | 0];
      for (var i = 0; i < count; i++) {
        var ang = (Math.PI * 2) * (i / count) + Math.random() * 0.25;
        var sp = 1.4 + Math.random() * 3.6;
        particles.push({
          x: x, y: y,
          vx: Math.cos(ang) * sp,
          vy: Math.sin(ang) * sp,
          life: 1,
          decay: 0.010 + Math.random() * 0.012,
          size: 1.4 + Math.random() * 1.8,
          color: Math.random() < 0.72 ? base : COLORS[Math.random() * COLORS.length | 0]
        });
      }
    }

    function backdropAlpha(e) {
      var fadeIn = 320, fadeOut = 900;
      if (e < fadeIn) return 0.46 * (e / fadeIn);
      if (e > TOTAL_MS - fadeOut) return 0.46 * Math.max(0, (TOTAL_MS - e) / fadeOut);
      return 0.46;
    }

    function frame(now) {
      var e = now - start;
      ctx.clearRect(0, 0, vw(), vh());

      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.fillStyle = 'rgba(6, 14, 34, ' + backdropAlpha(e).toFixed(3) + ')';
      ctx.fillRect(0, 0, vw(), vh());

      if (e < LAUNCH_MS && now - lastLaunch > 240) {
        lastLaunch = now;
        spawnShell();
        if (Math.random() < 0.55) spawnShell();
      }

      if (e < LAUNCH_MS && now - lastConfetti > 200) {
        lastConfetti = now;
        spawnConfetti(9, true);
      }

      var cFade = endFade(e);
      ctx.globalCompositeOperation = 'source-over';
      for (var k = confetti.length - 1; k >= 0; k--) {
        var c = confetti[k];
        c.wob += c.dwob;
        c.vy += 0.008;
        if (c.vy > 4.2) c.vy = 4.2;
        c.y += c.vy;
        c.x += c.vx + Math.sin(c.wob) * c.amp;
        c.rot += c.vrot;
        if (c.y > vh() + 30) { confetti.splice(k, 1); continue; }
        ctx.globalAlpha = cFade;
        ctx.save();
        ctx.translate(c.x, c.y);
        ctx.rotate(c.rot);
        ctx.fillStyle = c.color;
        ctx.fillRect(-c.w / 2, -c.h / 2, c.w, c.h);
        ctx.restore();
      }

      ctx.globalCompositeOperation = 'lighter';

      for (var i = shells.length - 1; i >= 0; i--) {
        var sh = shells[i];
        sh.vy += GRAVITY * 1.15;
        sh.y += sh.vy;
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = '#ffd780';
        ctx.beginPath();
        ctx.arc(sh.x, sh.y, 2, 0, Math.PI * 2);
        ctx.fill();
        if (sh.vy >= 0 || sh.y <= sh.targetY) {
          explode(sh.x, sh.y);
          shells.splice(i, 1);
        }
      }

      for (var j = particles.length - 1; j >= 0; j--) {
        var p = particles[j];
        p.vx *= 0.986;
        p.vy = p.vy * 0.986 + GRAVITY;
        p.x += p.vx;
        p.y += p.vy;
        p.life -= p.decay;
        if (p.life <= 0) { particles.splice(j, 1); continue; }
        ctx.globalAlpha = Math.max(p.life, 0);
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;

      var stillGoing = e < TOTAL_MS || particles.length || shells.length || confetti.length;
      if (stillGoing && e < TOTAL_MS + 2200) {
        window.requestAnimationFrame(frame);
      } else {
        window.removeEventListener('resize', resize);
        if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      }
    }
    window.requestAnimationFrame(frame);
  }

  // ---------------------------------------------------------------------------
  // Cart watcher: fire when a celebrate code is applicable, once per satisfy.
  // ---------------------------------------------------------------------------
  var firedThisPage = {};
  var checking = false;

  // Celebrate codes that are actually discounting the current cart.
  function satisfiedCodes(cart) {
    var out = [];
    var dcs = (cart && cart.discount_codes) || [];
    dcs.forEach(function (d) {
      if (d && d.applicable && isCelebrate(d.code)) out.push(up(d.code));
    });
    return out;
  }

  function reconcile(cart) {
    var satisfied = satisfiedCodes(cart);

    // Forget any code that dropped off, so it can celebrate again if re-added.
    Object.keys(firedThisPage).forEach(function (code) {
      if (satisfied.indexOf(code) === -1) delete firedThisPage[code];
    });
    var celebrated = getCelebrated().map(up);
    var stillValid = celebrated.filter(function (c) { return satisfied.indexOf(c) !== -1; });
    if (stillValid.length !== celebrated.length) setCelebrated(stillValid);

    // Anything newly satisfied that we haven't already celebrated -> fire.
    satisfied.forEach(function (code) {
      if (stillValid.indexOf(code) !== -1) return; // already celebrated, still valid
      if (firedThisPage[code]) return;             // already fired on this page
      firedThisPage[code] = true;
      var list = getCelebrated();
      if (list.map(up).indexOf(code) === -1) { list.push(code); setCelebrated(list); }
      launchFireworks();
    });
  }

  function checkCart() {
    if (checking) return;
    checking = true;
    fetch(CART_URL, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { reconcile(cart); })
      .catch(function () {})
      .then(function () { checking = false; });
  }

  // Detect cart mutations site-wide by watching cart AJAX endpoints.
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  if (window.fetch) {
    var _fetch = window.fetch;
    window.fetch = function () {
      var url = '';
      try { url = (typeof arguments[0] === 'string') ? arguments[0] : (arguments[0] && arguments[0].url) || ''; } catch (e) {}
      var pr = _fetch.apply(this, arguments);
      if (CART_WRITE.test(url)) {
        try { pr.then(function () { setTimeout(checkCart, 80); }).catch(function () {}); } catch (e) {}
      }
      return pr;
    };
  }

  if (window.XMLHttpRequest) {
    var _open = XMLHttpRequest.prototype.open;
    var _send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__wwUrl = url || '';
      return _open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      try {
        if (CART_WRITE.test(this.__wwUrl || '')) {
          this.addEventListener('load', function () { setTimeout(checkCart, 80); });
        }
      } catch (e) {}
      return _send.apply(this, arguments);
    };
  }

  // Bonus: also respond to common theme cart events (harmless if never fired).
  ['cart:updated', 'cart:refresh', 'cart:change', 'ajaxProduct:added', 'product:added']
    .forEach(function (evt) {
      document.addEventListener(evt, function () { setTimeout(checkCart, 80); });
    });

  // Initial load: covers discount-URL landings and carts already satisfied,
  // plus the reload after applying the code on the cart page.
  function init() { setTimeout(checkCart, 200); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Re-check when a page is restored from the back/forward cache.
  window.addEventListener('pageshow', function (e) { if (e.persisted) setTimeout(checkCart, 200); });
})();